import React, { useEffect, useRef, useState } from 'react';
import { Download, Film, Image, Loader2, Trash2, Upload } from 'lucide-react';
import DefaultSofiaOverlay from './sofiaoverlay.png';
import { mediaVideoRenderService } from '../../services/mediaVideoRenderService';
import { getMediaPackageStorageDownloadUrl, uploadMediaPackageStoryAsset } from './mediaPackageCloudStore';
import {
  canvasBlob,
  drawStoryVideoOverlay,
  ensureHeadlineFont,
  hexToRgba,
  loadCanvasImage,
  prepareStoryPhoto,
  safeFilename,
  triggerDownload,
} from './MediaAssetStudio';

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const drawOptionalStoryImageOverlay = (context, image, story) => {
  if (!image) return;
  const sizePercent = clamp(Number(story.image_overlay_size_percent) || 35, 5, 120);
  const width = context.canvas.width * (sizePercent / 100);
  const height = width * (image.height / image.width);
  const centreX = context.canvas.width * (clamp(Number(story.image_overlay_x_percent) || 50, 0, 100) / 100);
  const centreY = context.canvas.height * (clamp(Number(story.image_overlay_y_percent) || 38, 0, 100) / 100);
  context.save();
  context.drawImage(image, centreX - (width / 2), centreY - (height / 2), width, height);
  context.restore();
};

const drawPartnerLogoOnTop = (context, image, story) => {
  if (!image) return;
  const size = context.canvas.width * (clamp(Number(story.story_partner_logo_size_percent) || 16, 5, 35) / 100);
  const centreX = context.canvas.width * (clamp(Number(story.story_partner_logo_x_percent) || 88, 0, 100) / 100);
  const centreY = context.canvas.height * (clamp(Number(story.story_partner_logo_y_percent) || 9, 0, 100) / 100);
  const x = centreX - (size / 2);
  const y = centreY - (size / 2);
  const paddingPercent = story.story_partner_logo_padding_percent ?? 0;
  const contentScalePercent = story.story_partner_logo_content_scale_percent ?? 140;
  const padding = size * (clamp(Number(paddingPercent), 0, 35) / 100);
  const innerSize = Math.max(1, size - (padding * 2));
  const scale = Math.min(innerSize / image.width, innerSize / image.height)
    * (clamp(Number(contentScalePercent), 50, 300) / 100);
  const width = image.width * scale;
  const height = image.height * scale;

  context.save();
  context.beginPath();
  context.arc(centreX, centreY, size / 2, 0, Math.PI * 2);
  context.closePath();
  context.clip();
  context.fillStyle = '#ffffff';
  context.fillRect(x, y, size, size);
  context.drawImage(image, centreX - (width / 2), centreY - (height / 2), width, height);
  context.restore();
};

const InstagramStoryMediaItem = ({
  story,
  index,
  packageData,
  workspaceId,
  campaignImageSource,
  partnerLogoSource,
  onUpdate,
  onVisualHeadlineChange,
  onError,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isOverlayUploading, setIsOverlayUploading] = useState(false);
  const [overlayUploadProgress, setOverlayUploadProgress] = useState(0);
  const [isRendering, setIsRendering] = useState(false);
  const [renderProgress, setRenderProgress] = useState(0);
  const [renderElapsedSeconds, setRenderElapsedSeconds] = useState(0);
  const [renderError, setRenderError] = useState('');
  const [renderMessage, setRenderMessage] = useState('');
  const [resolvedRenderedVideoUrl, setResolvedRenderedVideoUrl] = useState(story.rendered_video_url || '');
  const [activeVisual, setActiveVisual] = useState('');
  const [visualInteraction, setVisualInteraction] = useState('');
  const [previewWidth, setPreviewWidth] = useState(352);
  const visualInteractionRef = useRef('');
  const previewRef = useRef(null);
  const headlineRef = useRef(null);
  const headlineSelectionRef = useRef(null);

  useEffect(() => {
    const preview = previewRef.current;
    if (!preview) return undefined;
    const updatePreviewWidth = () => setPreviewWidth(preview.getBoundingClientRect().width || 352);
    updatePreviewWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updatePreviewWidth);
      return () => window.removeEventListener('resize', updatePreviewWidth);
    }
    const observer = new ResizeObserver(updatePreviewWidth);
    observer.observe(preview);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isRendering) {
      setRenderElapsedSeconds(0);
      return undefined;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setRenderElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isRendering]);

  useEffect(() => {
    let cancelled = false;
    if (story.rendered_video_url) {
      setResolvedRenderedVideoUrl(story.rendered_video_url);
      return () => { cancelled = true; };
    }
    if (!story.rendered_video_storage_path) {
      setResolvedRenderedVideoUrl('');
      return () => { cancelled = true; };
    }
    getMediaPackageStorageDownloadUrl(story.rendered_video_storage_path)
      .then((url) => {
        if (!cancelled) setResolvedRenderedVideoUrl(url);
      })
      .catch(() => {
        if (!cancelled) setResolvedRenderedVideoUrl('');
      });
    return () => { cancelled = true; };
  }, [story.rendered_video_storage_path, story.rendered_video_url]);
  const mediaType = story.media_content_type || '';
  const isVideo = mediaType.startsWith('video/');
  const previewSource = story.media_url || campaignImageSource;
  const imageOverlaySource = story.image_overlay_disabled
    ? ''
    : story.image_overlay_url || DefaultSofiaOverlay;
  const isDefaultImageOverlay = Boolean(imageOverlaySource && !story.image_overlay_url);
  const storyLayout = {
    ...packageData.config,
    ...(packageData.config.asset_copy?.instagram_story || {}),
    visual_headline: story.visual_headline || story.title,
    visual_headline_segments: story.visual_headline_segments || [],
    visual_overtitle: story.visual_overtitle || '',
    visual_overtitle_color: story.visual_overtitle_color || '#57bfdc',
    visual_overtitle_size_percent: story.visual_overtitle_size_percent || 100,
    visual_headline_size_percent: story.visual_headline_size_percent
      ?? packageData.config.visual_headline_size_percent
      ?? packageData.config.asset_copy?.instagram_story?.visual_headline_size_percent
      ?? 100,
    visual_headline_x_percent: story.visual_headline_x_percent
      ?? packageData.config.asset_copy?.instagram_story?.visual_headline_x_percent
      ?? 7.5,
    visual_headline_y_percent: story.visual_headline_y_percent ?? 78,
    visual_logo_size_percent: story.story_partner_logo_size_percent ?? 16,
    visual_partner_logo_x_percent: story.story_partner_logo_x_percent ?? 88,
    visual_partner_logo_y_percent: story.story_partner_logo_y_percent ?? 9,
    visual_logo_content_scale_percent: story.story_partner_logo_content_scale_percent ?? 140,
    phone_partner_logo_padding_percent: story.story_partner_logo_padding_percent ?? 0,
  };
  const partnerLogoSize = clamp(Number(story.story_partner_logo_size_percent) || 16, 5, 35);
  const partnerLogoX = clamp(Number(story.story_partner_logo_x_percent) || 88, 0, 100);
  const partnerLogoY = clamp(Number(story.story_partner_logo_y_percent) || 9, 0, 100);
  const partnerLogoPadding = clamp(Number(story.story_partner_logo_padding_percent ?? 0), 0, 35);
  const partnerLogoContentScale = clamp(Number(story.story_partner_logo_content_scale_percent ?? 140), 50, 300);
  const overlaySize = clamp(Number(story.image_overlay_size_percent) || 35, 5, 120);
  const overlayX = clamp(Number(story.image_overlay_x_percent) || 50, 0, 100);
  const overlayY = clamp(Number(story.image_overlay_y_percent) || 38, 0, 100);
  const headlineSizePercent = clamp(Number(storyLayout.visual_headline_size_percent) || 100, 40, 220);
  const headlineSize = previewWidth * 0.083 * (headlineSizePercent / 100);
  const overtitleSize = headlineSize * 0.42 * (clamp(Number(storyLayout.visual_overtitle_size_percent) || 100, 40, 220) / 100);
  const headlineX = clamp(Number(storyLayout.visual_headline_x_percent) || 7.5, 3, 75);
  const headlineY = storyLayout.visual_headline_y_percent === ''
    || storyLayout.visual_headline_y_percent === null
    || storyLayout.visual_headline_y_percent === undefined
    ? 78
    : clamp(Number(storyLayout.visual_headline_y_percent), 5, 88);
  const renderBlockReason = !story.audio_url
    ? 'Generate the Story audio before rendering the MP4.'
    : story.audio_stale
      ? 'The narration changed. Regenerate its audio before rendering the MP4.'
      : (!story.media_storage_path && !campaignImageSource)
        ? 'Choose a Story image or video first.'
        : '';
  const storyOverlayStrength = clamp(Number(storyLayout.visual_overlay_strength) || 70, 20, 95) / 100;
  const storyGradientStyle = {
    background: `linear-gradient(to bottom, ${hexToRgba(storyLayout.visual_gradient_start_color, 0.05)} 20%, ${hexToRgba(storyLayout.visual_gradient_start_color, storyOverlayStrength * 0.7)} 64%, ${hexToRgba(storyLayout.visual_gradient_end_color, Math.min(0.98, storyOverlayStrength + 0.24))} 100%)`,
  };

  const startVisualInteraction = (event, interaction, visual) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    visualInteractionRef.current = interaction;
    setVisualInteraction(interaction);
    setActiveVisual(visual);
  };

  const stopVisualInteraction = () => {
    visualInteractionRef.current = '';
    setVisualInteraction('');
  };

  const moveVisual = (event) => {
    const interaction = visualInteractionRef.current;
    if (!interaction || !previewRef.current) return;
    const bounds = previewRef.current.getBoundingClientRect();
    if (interaction === 'titles-move') {
      updateVisualSetting({
        visual_headline_x_percent: Number(clamp(((event.clientX - bounds.left) / bounds.width) * 100, 3, 75).toFixed(2)),
        visual_headline_y_percent: Number(clamp(((event.clientY - bounds.top) / bounds.height) * 100, 5, 88).toFixed(2)),
      });
      return;
    }
    if (interaction === 'logo-move' || interaction === 'overlay-move') {
      const x = Number(clamp(((event.clientX - bounds.left) / bounds.width) * 100, 0, 100).toFixed(2));
      const y = Number(clamp(((event.clientY - bounds.top) / bounds.height) * 100, 0, 100).toFixed(2));
      updateVisualSetting(interaction === 'logo-move'
        ? { story_partner_logo_x_percent: x, story_partner_logo_y_percent: y }
        : { image_overlay_x_percent: x, image_overlay_y_percent: y });
      return;
    }
    if (interaction === 'logo-resize') {
      const centreX = bounds.left + ((partnerLogoX / 100) * bounds.width);
      const centreY = bounds.top + ((partnerLogoY / 100) * bounds.height);
      const diameter = Math.hypot(event.clientX - centreX, event.clientY - centreY) * 2;
      updateVisualSetting({
        story_partner_logo_size_percent: Number(clamp((diameter / bounds.width) * 100, 5, 35).toFixed(2)),
      });
      return;
    }
    if (interaction === 'overlay-resize') {
      const centreX = bounds.left + ((overlayX / 100) * bounds.width);
      const width = Math.abs(event.clientX - centreX) * 2;
      updateVisualSetting({
        image_overlay_size_percent: Number(clamp((width / bounds.width) * 100, 5, 120).toFixed(2)),
      });
    }
  };

  const adjustVisualSetting = (key, value, delta, minimum, maximum) => {
    updateVisualSetting({ [key]: clamp(Number(value) + delta, minimum, maximum) });
  };

  const commitImageText = (event, key) => {
    const rawValue = String(event.currentTarget.innerText || event.currentTarget.textContent || '');
    const value = key === 'visual_headline'
      ? rawValue.replace(/\r\n/g, '\n').replace(/[\t ]+/g, ' ').replace(/ *\n */g, '\n').replace(/\n{3,}/g, '\n\n').trim()
      : rawValue.replace(/\s+/g, ' ').trim();
    if (key === 'visual_headline') {
      const segments = extractHeadlineSegments(event.currentTarget);
      onVisualHeadlineChange?.(value, segments);
      return;
    }
    updateVisualSetting({ [key]: value });
  };

  const extractHeadlineSegments = (root) => {
    const segments = [];
    const visit = (node, inherited = {}) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.nodeValue) segments.push({ text: node.nodeValue, ...inherited });
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.tagName === 'BR') {
        segments.push({ text: '\n', ...inherited });
        return;
      }
      const style = { ...inherited };
      const tag = node.tagName.toLowerCase();
      if (tag === 'b' || tag === 'strong') style.fontWeight = '700';
      if (tag === 'i' || tag === 'em') style.fontStyle = 'italic';
      const elementStyle = node.style || {};
      const face = node.getAttribute?.('face');
      const color = node.getAttribute?.('color');
      if (face || elementStyle.fontFamily) style.fontFamily = face || elementStyle.fontFamily;
      if (color || elementStyle.color) style.color = color || elementStyle.color;
      if (elementStyle.fontWeight) style.fontWeight = elementStyle.fontWeight;
      if (elementStyle.fontStyle) style.fontStyle = elementStyle.fontStyle;
      Array.from(node.childNodes).forEach((child) => visit(child, style));
      if ((tag === 'div' || tag === 'p') && node.nextSibling) segments.push({ text: '\n', ...inherited });
    };
    Array.from(root.childNodes).forEach((node) => visit(node));
    return segments.filter((segment) => segment.text);
  };

  const rememberHeadlineSelection = () => {
    const selection = window.getSelection?.();
    if (!selection?.rangeCount || !headlineRef.current?.contains(selection.anchorNode)) return;
    const range = selection.getRangeAt(0);
    const before = range.cloneRange();
    before.selectNodeContents(headlineRef.current);
    before.setEnd(range.startContainer, range.startOffset);
    headlineSelectionRef.current = {
      start: before.toString().length,
      end: before.toString().length + range.toString().length,
    };
  };

  const applyHeadlineInlineStyle = (property, value) => {
    const selection = headlineSelectionRef.current;
    if (!selection || selection.end <= selection.start) return;
    const headline = story.visual_headline || story.title || '';
    const source = story.visual_headline_segments?.length
      ? story.visual_headline_segments
      : [{ text: headline }];
    const updated = [];
    let cursor = 0;
    source.forEach((segment) => {
      const text = String(segment.text || '');
      const segmentStart = cursor;
      const segmentEnd = cursor + text.length;
      const selectedStart = Math.max(selection.start, segmentStart);
      const selectedEnd = Math.min(selection.end, segmentEnd);
      if (selectedStart >= selectedEnd) updated.push(segment);
      else {
        const localStart = selectedStart - segmentStart;
        const localEnd = selectedEnd - segmentStart;
        if (localStart > 0) updated.push({ ...segment, text: text.slice(0, localStart) });
        updated.push({ ...segment, text: text.slice(localStart, localEnd), [property]: value });
        if (localEnd < text.length) updated.push({ ...segment, text: text.slice(localEnd) });
      }
      cursor = segmentEnd;
    });
    onVisualHeadlineChange?.(headline, updated.filter((segment) => segment.text));
  };

  const finishImageTextOnEnter = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.currentTarget.blur();
  };

  const chooseMedia = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const isVideoFile = file.type.startsWith('video/') || /\.(mp4|mov|m4v|webm)$/i.test(file.name);
    const isImageFile = file.type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!isVideoFile && !isImageFile) {
      const message = 'Choose an MP4, MOV, WebM, JPG, PNG, or WebP Story source.';
      setRenderError(message);
      setRenderMessage('');
      onError?.(message);
      return;
    }
    const limit = isVideoFile ? 300 * 1024 * 1024 : 15 * 1024 * 1024;
    if (file.size > limit) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      const message = `The selected ${isVideoFile ? 'video' : 'image'} is ${sizeMb} MB. The limit is ${isVideoFile ? '300' : '15'} MB.`;
      setRenderError(message);
      setRenderMessage('');
      onError?.(message);
      return;
    }
    setIsUploading(true);
    setUploadProgress(0);
    setRenderError('');
    setRenderMessage(`Uploading ${file.name}...`);
    onError?.('');
    try {
      const asset = await uploadMediaPackageStoryAsset({
        workspaceId,
        storyId: story.id,
        file,
        onProgress: setUploadProgress,
      });
      onUpdate({
        media_url: asset.url,
        media_storage_path: asset.storagePath,
        media_content_type: asset.contentType,
        media_fingerprint: asset.fingerprint,
        media_filename: file.name,
        rendered_video_url: '',
      });
      setRenderMessage(`${file.name} uploaded successfully.`);
    } catch (uploadError) {
      const message = uploadError.message || 'The Story media could not be uploaded.';
      setRenderError(message);
      setRenderMessage('');
      onError?.(message);
    } finally {
      setIsUploading(false);
    }
  };

  const removeMedia = () => onUpdate({
    media_url: '',
    media_storage_path: '',
    media_content_type: '',
    media_fingerprint: '',
    media_filename: '',
    rendered_video_url: '',
  });

  const chooseImageOverlay = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setRenderError('Choose a PNG, JPG, or WebP overlay image.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setRenderError('The overlay image is too large. Choose an image below 15 MB.');
      return;
    }
    setIsOverlayUploading(true);
    setOverlayUploadProgress(0);
    setRenderError('');
    setRenderMessage('');
    try {
      const asset = await uploadMediaPackageStoryAsset({
        workspaceId,
        storyId: story.id,
        file,
        assetSlot: 'image_overlay',
        onProgress: setOverlayUploadProgress,
      });
      onUpdate({
        image_overlay_url: asset.url,
        image_overlay_storage_path: asset.storagePath,
        image_overlay_content_type: asset.contentType,
        image_overlay_fingerprint: asset.fingerprint,
        image_overlay_filename: file.name,
        image_overlay_size_percent: story.image_overlay_size_percent ?? 35,
        image_overlay_x_percent: story.image_overlay_x_percent ?? 50,
        image_overlay_y_percent: story.image_overlay_y_percent ?? 38,
        image_overlay_disabled: false,
        rendered_video_url: '',
        rendered_video_storage_path: '',
      });
    } catch (uploadError) {
      const message = uploadError.message || 'The Story overlay image could not be uploaded.';
      setRenderError(message);
      onError?.(message);
    } finally {
      setIsOverlayUploading(false);
    }
  };

  const removeImageOverlay = () => onUpdate({
    image_overlay_url: '',
    image_overlay_storage_path: '',
    image_overlay_content_type: '',
    image_overlay_fingerprint: '',
    image_overlay_filename: '',
    image_overlay_disabled: true,
    rendered_video_url: '',
    rendered_video_storage_path: '',
  });

  const updateVisualSetting = (patch) => onUpdate({
    ...patch,
    rendered_video_url: '',
    rendered_video_storage_path: '',
  });

  const downloadRenderedVideo = async (videoUrl, filename) => {
    if (!videoUrl) throw new Error('No rendered MP4 is available yet.');
    try {
      const response = await fetch(videoUrl);
      if (!response.ok) throw new Error(`Download failed (${response.status}).`);
      triggerDownload(await response.blob(), filename);
    } catch (downloadError) {
      const anchor = document.createElement('a');
      anchor.href = videoUrl;
      anchor.download = filename;
      anchor.target = '_blank';
      anchor.rel = 'noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      if (!window.navigator.onLine) throw downloadError;
    }
  };

  const renderStory = async () => {
    if (isRendering) return;
    if (!story.audio_url || story.audio_stale) {
      const message = 'Approve the narration and generate fresh audio before rendering this Story.';
      setRenderError(message);
      onError?.(message);
      return;
    }
    if (!story.media_storage_path && !campaignImageSource) {
      const message = 'Choose an image or video for this Story.';
      setRenderError(message);
      onError?.(message);
      return;
    }
    setIsRendering(true);
    setRenderProgress(0);
    setRenderError('');
    setRenderMessage('Preparing the Story assets...');
    onError?.('');
    try {
      await ensureHeadlineFont();
      let partnerLogo = null;
      try { partnerLogo = await loadCanvasImage(partnerLogoSource); } catch { partnerLogo = null; }
      const overlayCanvas = document.createElement('canvas');
      overlayCanvas.width = 1080;
      overlayCanvas.height = 1920;
      drawStoryVideoOverlay(overlayCanvas.getContext('2d'), storyLayout, null, null, { includeCta: false });
      if (imageOverlaySource) {
        const imageOverlay = await loadCanvasImage(imageOverlaySource);
        drawOptionalStoryImageOverlay(overlayCanvas.getContext('2d'), imageOverlay, story);
      }
      drawPartnerLogoOnTop(overlayCanvas.getContext('2d'), partnerLogo, story);
      const overlayBlob = await canvasBlob(overlayCanvas);

      const sourceKind = story.media_storage_path
        ? isVideo ? 'video' : 'image'
        : 'image';
      const sourceFile = story.media_storage_path
        ? null
        : await prepareStoryPhoto(campaignImageSource, storyLayout);
      const sourceFingerprint = story.media_storage_path
        ? story.media_fingerprint || `${sourceKind}:${story.media_storage_path}`
        : `campaign:${String(campaignImageSource).length}:${story.id}`;
      const guideKey = `${packageData.guide.guide_id || packageData.config.guide_title || 'guide'}_${packageData.config.output_language || 'en'}`;
      const uploaded = await mediaVideoRenderService.uploadInputs({
        guideKey,
        workspaceId,
        sourceKey: story.id,
        sourceFile,
        sourceKind,
        overlayBlob,
        existingSourcePath: story.media_storage_path || '',
        sourceFingerprint,
        storedSourceFingerprint: story.media_fingerprint || '',
        onProgress: setRenderProgress,
      });
      setRenderMessage('');
      const filenameBase = `${safeFilename(packageData.config.guide_title)}_story_${index + 1}_${safeFilename(story.title)}`;
      const result = await mediaVideoRenderService.render({
        ...uploaded,
        sourceKind,
        workspaceId,
        audioUrl: story.audio_url,
        guideKey,
        outputFilename: filenameBase,
        durationSeconds: Number(packageData.config.video_duration_seconds) || 30,
        kenBurnsZoom: 1,
        kenBurnsDirection: 'still',
        colourWash: 'none',
        onProgress: setRenderProgress,
      });
      onUpdate({ rendered_video_url: result.videoUrl, rendered_video_storage_path: result.storagePath });
      setRenderMessage('Preview ready. Play it with sound, then download the MP4 when approved.');
    } catch (renderError) {
      const message = renderError.message || 'This Story video could not be rendered.';
      setRenderError(message);
      setRenderMessage('');
      onError?.(message);
    } finally {
      setIsRendering(false);
    }
  };

  const downloadExistingRender = async () => {
    const filenameBase = `${safeFilename(packageData.config.guide_title)}_story_${index + 1}_${safeFilename(story.title)}`;
    setRenderError('');
    setRenderMessage('Starting download...');
    try {
      await downloadRenderedVideo(resolvedRenderedVideoUrl, `${filenameBase}.mp4`);
      setRenderMessage('MP4 downloaded.');
    } catch (downloadError) {
      const message = downloadError.message || 'The saved MP4 could not be downloaded.';
      setRenderError(message);
      setRenderMessage('');
      onError?.(message);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="grid gap-5 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <div
          ref={previewRef}
          className={`relative aspect-[9/16] w-full max-w-[22rem] justify-self-center overflow-hidden rounded-lg bg-slate-950 shadow-sm lg:justify-self-start ${visualInteraction ? 'select-none' : ''}`}
          onPointerMove={moveVisual}
          onPointerUp={stopVisualInteraction}
          onPointerCancel={stopVisualInteraction}
          onPointerDown={() => setActiveVisual('')}
        >
          {isVideo ? (
            <video src={story.media_url} controls playsInline className="h-full w-full object-cover" />
          ) : previewSource ? (
            <img src={previewSource} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-slate-500"><Image className="h-8 w-8" /></div>
          )}
          <div className="pointer-events-none absolute inset-0" style={storyGradientStyle} />
          <div className="group/titles absolute z-10 text-white" style={{ left: `${headlineX}%`, right: '7.5%', top: `${headlineY}%` }}>
            <button
              type="button"
              aria-label="Move headline and over-title"
              title="Drag to move both titles horizontally and vertically"
              onPointerDown={(event) => startVisualInteraction(event, 'titles-move', 'headline')}
              className={`absolute -left-7 top-0 flex h-6 w-6 touch-none items-center justify-center rounded-full border border-white/70 bg-slate-950/80 text-xs font-bold text-white shadow-md ${visualInteraction === 'titles-move' ? 'cursor-grabbing ring-2 ring-fuchsia-400' : 'cursor-move'}`}
            >✥</button>
            <div className="relative mb-1 min-h-[1.2em]">
              {!story.visual_overtitle && <div className="pointer-events-none absolute inset-0 rounded border border-dashed border-white/50 px-1 text-white/60">Click to add over-title</div>}
              <div
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-label="Edit over-title directly on preview"
                title="Click and type to edit the over-title"
                onPointerDown={(event) => event.stopPropagation()}
                onFocus={() => setActiveVisual('overtitle')}
                onKeyDown={finishImageTextOnEnter}
                onBlur={(event) => commitImageText(event, 'visual_overtitle')}
                className="relative min-h-[1.2em] cursor-text rounded font-bold outline-none hover:ring-1 hover:ring-white/70 focus:bg-slate-950/40 focus:ring-2 focus:ring-fuchsia-400"
                style={{ color: story.visual_overtitle_color || '#57bfdc', fontFamily: 'Roboto, Arial, sans-serif', fontSize: `${overtitleSize}px`, lineHeight: 1 }}
              >{story.visual_overtitle || ''}</div>
            </div>
            <div
              key={JSON.stringify(story.visual_headline_segments || [])}
              ref={headlineRef}
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-label="Edit on-screen headline directly on preview"
              title="Click and type to edit the headline"
              onPointerDown={(event) => event.stopPropagation()}
              onFocus={() => setActiveVisual('headline')}
              onKeyUp={rememberHeadlineSelection}
              onMouseUp={rememberHeadlineSelection}
              onPaste={(event) => {
                event.preventDefault();
                document.execCommand('insertText', false, event.clipboardData.getData('text/plain'));
              }}
              onBlur={(event) => commitImageText(event, 'visual_headline')}
              className="cursor-text rounded font-bold outline-none hover:ring-1 hover:ring-white/70 focus:bg-slate-950/40 focus:ring-2 focus:ring-fuchsia-400"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: `${headlineSize}px`, lineHeight: 1.08, whiteSpace: 'pre-wrap' }}
            >{story.visual_headline_segments?.length
                ? story.visual_headline_segments.map((segment, segmentIndex) => (
                  <span key={`${segmentIndex}-${segment.text}`} style={{
                    color: segment.color || undefined,
                    fontFamily: segment.fontFamily || undefined,
                    fontWeight: segment.fontWeight || undefined,
                    fontStyle: segment.fontStyle || undefined,
                  }}>{segment.text}</span>
                ))
                : (story.visual_headline || story.title)}</div>
          </div>
          {imageOverlaySource && (
            <div
              role="button"
              tabIndex={0}
              aria-label="Drag extra image overlay"
              title="Drag to position. Pull the round handle to resize."
              onPointerDown={(event) => startVisualInteraction(event, 'overlay-move', 'overlay')}
              className={`group absolute z-30 touch-none cursor-grab ${visualInteraction === 'overlay-move' ? 'cursor-grabbing' : ''}`}
              style={{
                left: `${overlayX}%`,
                top: `${overlayY}%`,
                width: `${overlaySize}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <img src={imageOverlaySource} alt="" draggable="false" className={`pointer-events-none block h-auto w-full object-contain ${activeVisual === 'overlay' ? 'outline outline-2 outline-fuchsia-400' : 'group-hover:outline group-hover:outline-2 group-hover:outline-fuchsia-300'}`} />
              <button
                type="button"
                aria-label="Resize extra image overlay"
                title="Drag to resize overlay"
                onPointerDown={(event) => startVisualInteraction(event, 'overlay-resize', 'overlay')}
                className="absolute -bottom-2 -right-2 h-5 w-5 cursor-nwse-resize rounded-full border-2 border-white bg-fuchsia-600 shadow-md"
              />
            </div>
          )}
          {partnerLogoSource && (
            <div
              role="button"
              tabIndex={0}
              aria-label="Drag partner logo"
              title="Drag to position. Pull the round handle to resize."
              onPointerDown={(event) => startVisualInteraction(event, 'logo-move', 'logo')}
              className={`absolute z-40 flex aspect-square touch-none items-center justify-center rounded-full bg-white ${visualInteraction === 'logo-move' ? 'cursor-grabbing' : 'cursor-grab'} ${activeVisual === 'logo' ? 'ring-2 ring-fuchsia-400' : 'hover:ring-2 hover:ring-fuchsia-300'}`}
              style={{
                left: `${partnerLogoX}%`,
                top: `${partnerLogoY}%`,
                width: `${partnerLogoSize}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div className="absolute overflow-hidden rounded-full" style={{ inset: `${partnerLogoPadding}%` }}>
              <img
                src={partnerLogoSource}
                alt="Partner logo"
                draggable="false"
                className="pointer-events-none max-w-none shrink-0 object-contain"
                style={{ width: `${partnerLogoContentScale}%`, height: `${partnerLogoContentScale}%`, position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
              />
              </div>
              <button
                type="button"
                aria-label="Resize partner logo"
                title="Drag to resize partner logo"
                onPointerDown={(event) => startVisualInteraction(event, 'logo-resize', 'logo')}
                className="absolute -bottom-2 -right-2 z-10 h-5 w-5 cursor-nwse-resize rounded-full border-2 border-white bg-fuchsia-600 shadow-md"
              />
            </div>
          )}
          {activeVisual === 'logo' && partnerLogoSource && (
            <div className="absolute left-2 top-2 z-[60] rounded-lg border border-white/30 bg-slate-950/90 p-2 text-[10px] font-semibold text-white shadow-xl" onPointerDown={(event) => event.stopPropagation()}>
              <div className="grid grid-cols-[auto_auto_auto] items-center gap-x-2 gap-y-1.5">
                <span>Logo size</span><button type="button" aria-label="Reduce logo size" onClick={() => adjustVisualSetting('story_partner_logo_size_percent', partnerLogoSize, -1, 5, 35)} className="h-6 w-6 rounded bg-white/15 text-sm">−</button><button type="button" aria-label="Increase logo size" onClick={() => adjustVisualSetting('story_partner_logo_size_percent', partnerLogoSize, 1, 5, 35)} className="h-6 w-6 rounded bg-white/15 text-sm">+</button>
                <span>Artwork</span><button type="button" aria-label="Reduce logo artwork" onClick={() => adjustVisualSetting('story_partner_logo_content_scale_percent', partnerLogoContentScale, -5, 50, 300)} className="h-6 w-6 rounded bg-white/15 text-sm">−</button><button type="button" aria-label="Increase logo artwork" onClick={() => adjustVisualSetting('story_partner_logo_content_scale_percent', partnerLogoContentScale, 5, 50, 300)} className="h-6 w-6 rounded bg-white/15 text-sm">+</button>
                <span>Padding</span><button type="button" aria-label="Reduce logo padding" onClick={() => adjustVisualSetting('story_partner_logo_padding_percent', partnerLogoPadding, -1, 0, 35)} className="h-6 w-6 rounded bg-white/15 text-sm">−</button><button type="button" aria-label="Increase logo padding" onClick={() => adjustVisualSetting('story_partner_logo_padding_percent', partnerLogoPadding, 1, 0, 35)} className="h-6 w-6 rounded bg-white/15 text-sm">+</button>
              </div>
            </div>
          )}
          {activeVisual === 'overlay' && imageOverlaySource && (
            <div className="absolute left-2 top-2 z-[60] flex items-center gap-2 rounded-lg border border-white/30 bg-slate-950/90 p-2 text-[10px] font-semibold text-white shadow-xl" onPointerDown={(event) => event.stopPropagation()}>
              <span>Overlay size</span>
              <button type="button" aria-label="Reduce overlay size" onClick={() => adjustVisualSetting('image_overlay_size_percent', overlaySize, -2, 5, 120)} className="h-6 w-6 rounded bg-white/15 text-sm">−</button>
              <button type="button" aria-label="Increase overlay size" onClick={() => adjustVisualSetting('image_overlay_size_percent', overlaySize, 2, 5, 120)} className="h-6 w-6 rounded bg-white/15 text-sm">+</button>
            </div>
          )}
          {activeVisual === 'overtitle' && (
            <div className="absolute left-2 top-2 z-[60] flex items-center gap-2 rounded-lg border border-white/30 bg-slate-950/90 p-2 text-[10px] font-semibold text-white shadow-xl" onPointerDown={(event) => event.stopPropagation()}>
              <span>Over-title size</span>
              <button type="button" aria-label="Reduce over-title size" onClick={() => adjustVisualSetting('visual_overtitle_size_percent', storyLayout.visual_overtitle_size_percent, -5, 40, 220)} className="h-6 w-6 rounded bg-white/15 text-sm">−</button>
              <button type="button" aria-label="Increase over-title size" onClick={() => adjustVisualSetting('visual_overtitle_size_percent', storyLayout.visual_overtitle_size_percent, 5, 40, 220)} className="h-6 w-6 rounded bg-white/15 text-sm">+</button>
              <label className="ml-1 flex items-center gap-1.5">Colour<input type="color" aria-label="Over-title colour" value={story.visual_overtitle_color || '#57bfdc'} onChange={(event) => updateVisualSetting({ visual_overtitle_color: event.target.value })} className="h-6 w-8 cursor-pointer rounded border border-white/40 bg-transparent p-0" /></label>
            </div>
          )}
          {activeVisual === 'headline' && (
            <div className="absolute left-2 top-2 z-[60] flex flex-wrap items-center gap-2 rounded-lg border border-white/30 bg-slate-950/90 p-2 text-[10px] font-semibold text-white shadow-xl" onPointerDown={(event) => event.stopPropagation()}>
              <span>Headline size</span>
              <button type="button" aria-label="Reduce headline size" onClick={() => adjustVisualSetting('visual_headline_size_percent', headlineSizePercent, -5, 40, 220)} className="h-6 w-6 rounded bg-white/15 text-sm">−</button>
              <button type="button" aria-label="Increase headline size" onClick={() => adjustVisualSetting('visual_headline_size_percent', headlineSizePercent, 5, 40, 220)} className="h-6 w-6 rounded bg-white/15 text-sm">+</button>
              <span className="ml-1 border-l border-white/30 pl-2">Selected text</span>
              <button type="button" title="Italic" onMouseDown={(event) => event.preventDefault()} onClick={() => applyHeadlineInlineStyle('fontStyle', 'italic')} className="h-6 min-w-6 rounded bg-white/15 px-1.5 text-xs italic">I</button>
              <select aria-label="Selected text weight" defaultValue="700" onMouseDown={(event) => event.stopPropagation()} onChange={(event) => applyHeadlineInlineStyle('fontWeight', event.target.value)} className="h-6 rounded border border-white/30 bg-slate-900 px-1 text-[10px] text-white">
                <option value="200">Thin</option><option value="300">Light</option><option value="400">Regular</option><option value="500">Medium</option><option value="600">Semi-bold</option><option value="700">Bold</option><option value="800">Extra-bold</option>
              </select>
              <select aria-label="Selected text typeface" defaultValue="Playfair Display" onMouseDown={(event) => event.stopPropagation()} onChange={(event) => applyHeadlineInlineStyle('fontFamily', event.target.value)} className="h-6 rounded border border-white/30 bg-slate-900 px-1 text-[10px] text-white">
                <option>Playfair Display</option><option>Roboto</option><option>Georgia</option><option>Arial</option>
              </select>
              <label className="flex items-center gap-1">Colour<input type="color" aria-label="Selected text colour" defaultValue="#ffffff" onPointerDown={(event) => event.stopPropagation()} onChange={(event) => applyHeadlineInlineStyle('color', event.target.value)} className="h-6 w-8 cursor-pointer rounded border border-white/40 bg-transparent p-0" /></label>
            </div>
          )}
        </div>
        <div className="flex flex-col justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-fuchsia-700">{isVideo ? <Film className="h-4 w-4" /> : <Image className="h-4 w-4" />}{story.media_filename || (previewSource ? 'Package image' : 'No media selected')}</div>
            <p className="mt-1 text-xs leading-5 text-slate-500">This image or video belongs only to Story {index + 1}. Its approved audio is used automatically for the MP4.</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">GuideSofia branding</div>
                <div className="mt-0.5 text-xs text-slate-500">Optional transparent PNG, JPG, or WebP layered over this Story.</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-fuchsia-300 bg-white px-3 py-2 text-xs font-semibold text-fuchsia-800">
                  <Upload className="h-3.5 w-3.5" />
                  {isOverlayUploading ? `Uploading ${overlayUploadProgress}%` : imageOverlaySource ? 'Replace branding' : 'Add branding'}
                  <input className="hidden" type="file" accept="image/png,image/webp,image/jpeg" disabled={isOverlayUploading} onChange={chooseImageOverlay} />
                </label>
                {imageOverlaySource && <button type="button" onClick={removeImageOverlay} className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700"><Trash2 className="h-3.5 w-3.5" />Remove</button>}
                {!imageOverlaySource && <button type="button" onClick={() => updateVisualSetting({ image_overlay_disabled: false })} className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-800">Restore Sofia overlay</button>}
              </div>
            </div>
            {isDefaultImageOverlay && <div className="mt-2 text-xs font-medium text-sky-700">Using the default sofiaoverlay.png.</div>}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-fuchsia-300 bg-white px-3 py-2 text-xs font-semibold text-fuchsia-800"><Upload className="h-3.5 w-3.5" />{isUploading ? `Uploading ${uploadProgress}%` : story.media_url ? 'Replace media' : 'Choose media'}<input className="hidden" type="file" accept="video/*,image/*" disabled={isUploading} onChange={chooseMedia} /></label>
            {story.media_url && <button type="button" onClick={removeMedia} className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700"><Trash2 className="h-3.5 w-3.5" />Use package image</button>}
            <button type="button" onClick={renderStory} disabled={isRendering || Boolean(renderBlockReason)} className="flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:bg-slate-300 sm:col-span-2">{isRendering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />}{isRendering ? renderProgress < 60 ? `Preparing ${renderProgress}%` : 'Rendering preview MP4...' : resolvedRenderedVideoUrl ? 'Render updated preview' : 'Render Story preview'}</button>
          </div>
          {isRendering && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-3">
              <div className="flex items-center justify-between gap-3 text-xs font-semibold text-sky-900">
                <span>{renderProgress < 48 ? 'Uploading Story media' : renderProgress < 60 ? 'Uploading visual composition' : 'Rendering video on the server'}</span>
                <span>{renderProgress < 60 ? `${renderProgress}% · ` : ''}{Math.floor(renderElapsedSeconds / 60)}:{String(renderElapsedSeconds % 60).padStart(2, '0')}</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-sky-100">
                <div className={`h-full rounded-full bg-sky-600 transition-[width] duration-700 ${renderProgress >= 60 ? 'animate-pulse' : ''}`} style={{ width: `${Math.max(2, renderProgress)}%` }} />
              </div>
              {renderProgress >= 60 && <p className="mt-2 text-xs leading-5 text-sky-700">Encoding the MP4. The timer shows elapsed time; the server does not provide a progress percentage.</p>}
            </div>
          )}
          {renderBlockReason && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">{renderBlockReason}</p>}
          {renderMessage && <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800">{renderMessage}</p>}
          {renderError && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-800">{renderError}</p>}
        </div>
      </div>
      {resolvedRenderedVideoUrl && (
        <div className="mt-4 overflow-hidden rounded-xl border border-emerald-200 bg-slate-950">
          <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 text-white">
            <div>
              <div className="text-sm font-semibold">Final Story preview</div>
              <div className="mt-0.5 text-xs text-slate-300">Includes the background movement, approved audio, text, overlay, and partner logo.</div>
            </div>
            <button type="button" onClick={downloadExistingRender} className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-500"><Download className="h-3.5 w-3.5" />Download MP4</button>
          </div>
          <div className="flex justify-center bg-black p-4">
            <video src={resolvedRenderedVideoUrl} controls playsInline preload="metadata" className="max-h-[70vh] max-w-full rounded-lg bg-black shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramStoryMediaItem;
